import { useMemo, useCallback, useRef, useEffect } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { eventToAvi } from '@confluence/pubsub-client/entry-points/PubSubClient.ts';
import { getPubSubClient } from '@confluence/pubsub-client/entry-points/client.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver.ts';
import { getSortedMarkerRefs } from '@confluence/comments-util/entry-points/domUtils.ts';
import { useAnnotations } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	useCommentsData,
	CommentActionType,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';

import {
	InlineCommentPubSubEvents,
	InlineCommentSubscriptionEvents,
} from '../CommentPubSubEvents.ts';
import {
	refreshCommentThread,
	handleAddedReplyInCommentThread,
	handleAddNewCommentThread,
	handleEditedComment,
} from '../utils/pubsubUtils.ts';

type BasePubsubProps = {
	contentId: string;
	contentType?: string | null;
};

export type PubSubProps = BasePubsubProps & {
	triggerAnalyticsEvent?:
		| ((
				eventName: string,
				extraData?: {
					[key: string]: any;
				},
		  ) => Promise<void>)
		| CreateUIAnalyticsEvent;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	storedCommentData?: any;
	setStoredCommentData?: (commentData: any) => void;
	addCommentInCache?: ({
		newComment,
		onError,
	}: {
		newComment: any;
		onError?: (error: Error) => void;
	}) => void;
	deleteCommentInCache?: ({
		commentId,
		onError,
	}: {
		commentId: string;
		onError?: (error: Error) => void;
	}) => void;
};

export const useEditorInlineCommentsPubSub = ({
	contentId,
	contentType,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	storedCommentData,
	setStoredCommentData,
	addCommentInCache,
	deleteCommentInCache,
}: PubSubProps) => {
	const pubSubResourceType = contentType || 'page';
	const subscriptions = useRef<{ delta: string; subscriberId: string }[]>([]);

	const [
		{ orderedActiveAnnotationIdList },
		{
			addReplyToCommentThread,
			addNewCommentThreads,
			updateRemovedCommentIdsMap,
			updateCommentBody,
			reopenParentComment,
		},
	] = useCommentsData();
	const { userId: currentUserId } = useSessionData();
	const orderedActiveAnnotationIdListRef = useRef(orderedActiveAnnotationIdList);
	const { editorViewProps } = useAnnotations();
	const { setContentId, setContentType, addSubscriber, removeSubscriber } =
		useSubscriptionsDispatchContext() || {};
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();

	const { annotations = [] } = editorViewProps || {};
	const selectedAnnotationId = annotations[0]?.id;

	useEffect(() => {
		orderedActiveAnnotationIdListRef.current = orderedActiveAnnotationIdList;
	}, [orderedActiveAnnotationIdList]);

	const handleOnPublicationEvent = useCallback(
		(event: string, markerRef: any) => {
			const emitter = getEditorAnnotationEventEmitter();
			emitter.emit(event, markerRef);
			const annotationIds = orderedActiveAnnotationIdListRef.current.map(
				(annotation) => annotation.threadKey,
			);

			if (triggerAnalyticsEvent) {
				// Updates ordered list of unresolved inline comments based on ADF to account for real-time changes (collaborative editing)
				void getEditorResolvedState({
					// the annotations won't already include the pubsub marker ref
					annotations: getSortedMarkerRefs([...annotationIds, markerRef], 'edit'),
					pageId: contentId,
					triggerAnalyticsEvent,
					updateUnresolvedInlineComment,
				});
			}
		},
		[contentId, triggerAnalyticsEvent, updateUnresolvedInlineComment],
	);

	const onPublicationEvent = useCallback(
		async (event: string, payload: any) => {
			if (!event) {
				return;
			}
			let inlineCommentRequestBody;
			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('cc_fps_migration_comments') &&
				fg('confluence_enable_subscriptions-context-provider')
			) {
				inlineCommentRequestBody = payload?.[event];
			} else {
				inlineCommentRequestBody = payload.inlineCommentRequestBody;
			}
			const { accountId } = payload || {};
			const { markerRef, commentId, inlineCommentType } = inlineCommentRequestBody || {};

			// Don't do anything when the current user performs an action
			if (accountId === currentUserId) {
				return;
			}

			const isReply = inlineCommentType === 'REPLY';

			switch (event) {
				case InlineCommentSubscriptionEvents.InlineCommentUnresolved:
				case InlineCommentPubSubEvents.InlineCommentUnresolved:
					if (fg('confluence-frontend-comments-panel')) {
						if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							if (commentId) {
								// fetchCommentById implicitly updates useAllCommentsDataQuery cache
								await fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
							}
						} else {
							// We load resolved + active separately, so unless the user has visited the resolved tab, the comment thread won't be in the state
							// Hence the await here - as part of this function we update state
							await handleAddNewCommentThread({
								contentId,
								markerRef,
								addNewCommentThreads,
							});
						}
						// Remove from commentThreadsRemovedByOtherUsers when reopened
						if (
							markerRef &&
							reopenParentComment &&
							fg('confluence_comments_reply_inline_reliability')
						) {
							reopenParentComment({
								threadKey: markerRef,
								commentType: CommentType.INLINE,
							});
						}
					}
					// Call handleOnPublicationEvent after store updates are complete
					handleOnPublicationEvent('unresolve', markerRef);
					break;
				case InlineCommentSubscriptionEvents.InlineCommentResolved:
				case InlineCommentPubSubEvents.InlineCommentResolved:
					handleOnPublicationEvent('resolve', markerRef);
					if (fg('confluence-frontend-comments-panel')) {
						if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							if (commentId) {
								// fetchCommentById implicitly updates useAllCommentsDataQuery cache
								void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
							}
						}
						const { inlineResolveProperties, inlineText } = inlineCommentRequestBody;

						if (
							inlineResolveProperties &&
							(inlineText || fg('confluence_comments_reply_inline_reliability'))
						) {
							updateRemovedCommentIdsMap({
								threadKey: markerRef,
								commentId,
								commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
								isReply,
								resolvedProperties: {
									inlineResolveProperties,
									inlineText,
								},
								commentType: CommentType.INLINE,
							});
						}

						const { replies = [] } = inlineCommentRequestBody;

						if (updateUnreadCommentIDSet) {
							updateUnreadCommentIDSet(new Set([commentId, ...replies]), 'remove');
						}
					}

					break;
				case InlineCommentSubscriptionEvents.InlineCommentCreated:
				case InlineCommentSubscriptionEvents.EditorInlineCommentCreated:
				case InlineCommentPubSubEvents.InlineCommentCreated:
				case InlineCommentPubSubEvents.EditorInlineCommentCreated:
					if (fg('confluence-frontend-comments-panel')) {
						if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							if (addCommentInCache && commentId) {
								const comment = await fetchCommentById({
									commentId,
									contentId,
									fetchPolicy: 'network-only',
								});
								if (comment) {
									addCommentInCache({ newComment: comment });
								}
							}
						} else {
							if (isReply) {
								await handleAddedReplyInCommentThread({
									contentId,
									markerRef,
									commentId,
									addReplyToCommentThread,
									commentType: CommentType.INLINE,
									storedCommentData,
									setStoredCommentData,
								});
							} else {
								await handleAddNewCommentThread({
									contentId,
									markerRef,
									addNewCommentThreads,
								});
							}
						}

						if (updateUnreadCommentIDSet) {
							updateUnreadCommentIDSet(new Set([commentId]), 'add');
						}
					} else {
						if (isReply && markerRef === selectedAnnotationId) {
							void refreshCommentThread(contentId, markerRef);
						}
					}

					// Call handleOnPublicationEvent after store updates are complete
					handleOnPublicationEvent('create', markerRef);
					break;
				case InlineCommentSubscriptionEvents.InlineCommentDeleted:
				case InlineCommentPubSubEvents.InlineCommentDeleted:
					// both a parent comment and reply can be deleted
					if (fg('confluence-frontend-comments-panel')) {
						updateRemovedCommentIdsMap({
							threadKey: markerRef,
							commentId,
							isReply,
							commentActionType: CommentActionType.DELETE_COMMENT,
							commentType: CommentType.INLINE,
						});

						if (updateUnreadCommentIDSet) {
							updateUnreadCommentIDSet(new Set([commentId]), 'remove');
						}
					}

					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (deleteCommentInCache && commentId) {
							deleteCommentInCache({ commentId });
						}
					}

					// Always handle annotation logic
					// We update useCommentsData state for the full panel, but the standalone comment doesn't look at it, so keep in sync via the API
					if (isReply && markerRef === selectedAnnotationId) {
						void refreshCommentThread(contentId, markerRef);
					}
					if (!isReply && fg('confluence_frontend_new_dangling_comments_ux')) {
						handleOnPublicationEvent('delete', markerRef);
					}

					break;
				case InlineCommentSubscriptionEvents.InlineCommentUpdated:
				case InlineCommentPubSubEvents.InlineCommentUpdated:
					if (fg('confluence-frontend-comments-panel')) {
						if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							if (commentId) {
								// fetchCommentById implicitly updates useAllCommentsDataQuery cache
								void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
							}
						} else {
							void handleEditedComment({
								contentId,
								markerRef,
								commentId,
								updateCommentBody,
							});
						}
					} else {
						if (markerRef === selectedAnnotationId) {
							void refreshCommentThread(contentId, markerRef);
						}
					}
					break;
				default:
					break;
			}
		},
		[
			contentId,
			handleOnPublicationEvent,
			selectedAnnotationId,
			addNewCommentThreads,
			addReplyToCommentThread,
			updateRemovedCommentIdsMap,
			currentUserId,
			updateCommentBody,
			storedCommentData,
			setStoredCommentData,
			updateUnreadCommentIDSet,
			addCommentInCache,
			deleteCommentInCache,
			reopenParentComment,
		],
	);

	const register = useCallback(() => {
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
			if (!setContentType || !setContentId || !addSubscriber) {
				return;
			}
			setContentType(pubSubResourceType);
			setContentId(contentId);

			const subscribers = [
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentResolved',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentResolved',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentUnresolved',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentUnresolved',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentCreated',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentCreated',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubInlineCommentUpdated',
					onSubscriptionData: onPublicationEvent,
					delta: 'inlineCommentUpdated',
				},
				{
					subscriberId: 'useEditorInlineCommentsPubSubEditorInlineCommentCreated',
					onSubscriptionData: onPublicationEvent,
					delta: 'editorInlineCommentCreated',
				},
			];

			for (const subscriber of subscribers) {
				subscriptions.current.push({
					subscriberId: addSubscriber(subscriber),
					delta: subscriber.delta,
				});
			}
			if (fg('confluence-frontend-comments-panel')) {
				subscriptions.current.push({
					delta: 'inlineCommentDeleted',
					subscriberId: addSubscriber({
						subscriberId: 'useEditorInlineCommentsPubSubInlineCommentDeleted',
						onSubscriptionData: onPublicationEvent,
						delta: 'inlineCommentDeleted',
					}),
				});
			}
		} else {
			void getPubSubClient().then((client) => {
				client
					.joinChannel(pubSubResourceType, contentId)
					.finally(() => {
						void client.subscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

						void client.subscribe(eventToAvi('updated:inline_comment'), onPublicationEvent);

						if (fg('confluence-frontend-comments-panel')) {
							void client.subscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
						}
					})
					.catch((error) => {
						throw new Error(`PubSub Web Client joinChannel ${JSON.stringify(error)}`);
					});
			});
		}
	}, [
		setContentType,
		pubSubResourceType,
		setContentId,
		contentId,
		onPublicationEvent,
		subscriptions,
		addSubscriber,
	]);

	const unregister = useCallback(() => {
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
			for (const subscriber of subscriptions.current) {
				removeSubscriber?.(subscriber);
			}
		} else {
			void getPubSubClient().then((client) => {
				void client.leaveChannel(pubSubResourceType, contentId);
				void client.unsubscribe(eventToAvi('resolved:inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('unresolved:inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('created:inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('created:editor_inline_comment'), onPublicationEvent);

				void client.unsubscribe(eventToAvi('updated:inline_comment'), onPublicationEvent);

				if (fg('confluence-frontend-comments-panel')) {
					void client.unsubscribe(eventToAvi('deleted:inline_comment'), onPublicationEvent);
				}
			});
		}
	}, [contentId, onPublicationEvent, pubSubResourceType, removeSubscriber, subscriptions]);

	return useMemo(
		() => ({
			register,
			unregister,
			onPublicationEvent,
		}),
		[register, unregister, onPublicationEvent],
	);
};
